import {
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";

const FONT_SIZE = "0.875rem";
const EMPTY = "—";
// premiums, rates and factors share the table, so decimals must not be padded to 4
const formatNumber = numbers.dpString(4, { minDp: 0, nullValue: EMPTY });

const prettifyKey = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const labelFor = (key, resultLabels) => resultLabels?.[key] ?? prettifyKey(key);

const formatValue = (value) => {
  if (value == null || value === "") {
    return EMPTY;
  }
  return typeof value === "number" ? formatNumber(value) : String(value);
};

const ModelPricingResults = ({
  pricing,
  resultLabels,
  isStale,
  highlightRecordIndex,
  maxRecords,
  // Show one record instead of the table. The workbook prices every layer, but the tab looks at
  // one at a time, so the rows for the others would only be noise.
  singleRecordIndex,
}) => {
  const failure = pricing?.failure;
  const values = pricing?.values ?? {};
  const errors = pricing?.errors ?? {};

  const namedOutputs = Object.entries(values).filter(
    ([key]) => key !== "records"
  );
  const allRecords = Array.isArray(values.records) ? values.records : [];
  // a workbook returns every row of its table, including the ones no input filled in
  const trimmed =
    maxRecords != null ? allRecords.slice(0, maxRecords) : allRecords;
  const singleRecord =
    singleRecordIndex != null ? trimmed[singleRecordIndex] : null;
  const records = singleRecordIndex != null ? [] : trimmed;
  const recordColumns = [
    ...new Set(records.flatMap((record) => Object.keys(record ?? {}))),
  ];
  const singleRecordRows = Object.entries(singleRecord ?? {});
  const cellErrors = Object.entries(errors);

  if (
    !failure &&
    !namedOutputs.length &&
    !records.length &&
    !singleRecordRows.length &&
    !cellErrors.length
  ) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {isStale && (
        <Alert severity={"info"} sx={{ width: "100%" }}>
          {
            "These results are from the last successful calculation — fix the highlighted inputs and recalculate."
          }
        </Alert>
      )}
      {failure && (
        <Alert severity={"error"} sx={{ width: "100%" }}>
          {failure}
        </Alert>
      )}
      {cellErrors.length > 0 && (
        <Alert severity={"warning"} sx={{ width: "100%" }}>
          <Typography fontSize={FONT_SIZE} fontWeight={600}>
            {"Workbook cell errors"}
          </Typography>
          {cellErrors.map(([cell, label]) => (
            <Typography key={cell} fontSize={FONT_SIZE}>
              {`${cell}: ${label}`}
            </Typography>
          ))}
        </Alert>
      )}
      {singleRecordRows.length > 0 && (
        <TableContainer>
          <Table size={"small"}>
            <TableBody>
              {singleRecordRows.map(([key, value]) => (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontSize: FONT_SIZE }}>
                    {labelFor(key, resultLabels)}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: FONT_SIZE, fontWeight: 600 }}
                    align={"right"}
                  >
                    {formatValue(value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {namedOutputs.length > 0 && (
        <TableContainer>
          <Table size={"small"}>
            <TableBody>
              {namedOutputs.map(([key, value]) => (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontSize: FONT_SIZE }}>
                    {labelFor(key, resultLabels)}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: FONT_SIZE, fontWeight: 600 }}
                    align={"right"}
                  >
                    {formatValue(value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {records.length > 0 && (
        <TableContainer>
          <Table size={"small"}>
            <TableHead>
              <TableRow>
                {recordColumns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{ fontSize: FONT_SIZE, fontWeight: 600 }}
                  >
                    {labelFor(column, resultLabels)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record, index) => (
                <TableRow
                  key={index}
                  hover
                  selected={index === highlightRecordIndex}
                >
                  {recordColumns.map((column) => (
                    <TableCell key={column} sx={{ fontSize: FONT_SIZE }}>
                      {formatValue(record?.[column])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default ModelPricingResults;
